import type { Content, EntityType } from "../content/model";
import { valueAt } from "../content/model";

// THE VIEW SPEC: a view as data, not as code.
//
// Every unit and lesson view is produced as one of these, validated against the component
// registry, and then drawn by `render.tsx`. Three producers can make one — the schema-derived
// default, an authored file, and one day a model — and the renderer cannot tell which it got.
// That is the point: it is the seam a generated view plugs into without anything downstream
// changing.
//
// WHY DATA AND NOT REACT ELEMENTS. A producer that returned elements would be a nicer way to
// hard-code a screen and nothing more: you cannot cache an element, send one over a wire,
// validate one, diff two, or ask a model for one. The intermediate representation being
// serialisable is the whole difference between this and a refactor.
//
// WHY BINDINGS AND NOT VALUES. A spec never contains content. `{"$bind": "lesson.skill"}` is
// reusable across every lesson of the same shape; "Evaluating claims and evidence" is good for
// exactly one. That matters most for the producer that does not exist yet: a generated spec with
// content baked into it costs one model call per lesson and can never be cached, while a bound
// one is generated once for a kind of thing and reused. Bindings are what make generation
// affordable, which is why they are here from the start.
//
// WHAT IS DELIBERATELY NOT HERE. Loops, conditions beyond present/absent, expressions, arithmetic,
// formatting directives. Paths and "render if present" are a FORMAT. The moment a spec can compute
// something it is a language, and a language in JSON is a language with no debugger, no types and
// no tests. A component that needs a list takes the list as a prop and renders it itself — which
// is also why the registry's components are mid-level rather than primitives.

export const SPEC_FORMAT_VERSION = 1;

/** A prop is a literal, or a binding to content. Nothing else. */
export type PropValue = string | number | boolean | null | Binding;

export interface Binding {
  /** A path into content: `lesson.skill`, `unit.standards`, `blocks`, `context.courseName`. */
  $bind: string;
}

export function isBinding(v: unknown): v is Binding {
  return typeof v === "object" && v !== null && typeof (v as Binding).$bind === "string";
}

export interface SpecNode {
  component: string;
  props?: Record<string, PropValue>;
  children?: SpecNode[];
  /** Render this node only when the bound path has a value. The one conditional there is: it
   *  answers "does this unit have texts?", which is the only question the screens actually ask
   *  of content. Anything richer belongs in a component, where it can be tested. */
  $when?: { present: string };
}

export type Producer = "default" | "authored" | "generated";

export interface ViewSpec {
  id: string;
  producer: Producer;
  createdAt: string;
  schemaVersion: number;
  registryVersion: number;
  /** What this spec is a view OF, and for whom. The validator checks bindings against this
   *  entity's schema, so a lesson spec cannot bind to a unit-only field. */
  entityType: EntityType;
  audience: Audience;
  intent: Intent;
  root: SpecNode;
}

export type Audience = "teacher";
export type Intent = "overview" | "detail";

export interface ViewContext {
  entityType: EntityType;
  audience: Audience;
  intent: Intent;
  schemaVersion: number;
}

/** The cache key, and the reason both versions are in it.
 *
 *  A spec is only valid against the component registry it was produced for and the content schema
 *  it binds to. Bump either and the old spec may name a component that no longer exists or a field
 *  that has been renamed — so it must not be served from a cache. Putting both versions in the key
 *  makes that automatic rather than something anybody has to remember to invalidate. */
export function specKey(ctx: ViewContext, registryVersion: number): string {
  return [ctx.entityType, ctx.audience, ctx.intent,
          `s${ctx.schemaVersion}`, `r${registryVersion}`].join(":");
}

/** Resolve one prop against content. Literals pass through; bindings are looked up. */
export function resolveProp(value: PropValue, content: Content): unknown {
  return isBinding(value) ? valueAt(content, value.$bind) : value;
}

export function resolveProps(props: Record<string, PropValue> | undefined,
                             content: Content): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props ?? {})) out[k] = resolveProp(v, content);
  return out;
}

/** Is a `$when` satisfied? Absent, empty string and empty array all count as absent — a unit with
 *  `texts: []` has no texts, and a screen that showed an empty "Texts" heading for it would be
 *  reporting the shape of the data rather than the state of the world. */
export function shows(node: SpecNode, content: Content): boolean {
  if (!node.$when) return true;
  const v = valueAt(content, node.$when.present);
  return !(v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0));
}
